/* eslint-disable react/no-unescaped-entities */
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { getFeaturedNewsAndEvents } from '../../services/news';
import Carousel from '../../components/molecules/carousel/carousel';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin-bottom: 15px;
    text-align: justify;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

const links = {
  showroom: 'https://my.matterport.com/show/?m=Boe2AFEuzJe',
  dualRefSystemArticle: 'https://subzero-wolf.com.ph/news-and-events/26659',
  inductionArticle: 'https://subzero-wolf.com.ph/news-and-events/26657',
  bookAnAppointment:
    'https://calendly.com/fgi-kitchen-bookings?utm_source=Instagram&utm_medium=Bio_Link&utm_campaign=Calendly_Appointments&utm_content=SZWF'
};

const carouselImages = [
  `${strapiAssetBaseUrl}/1_4f53aaf4f1.png`,
  `${strapiAssetBaseUrl}/2_03d6ccddc7.png`,
  `${strapiAssetBaseUrl}/3_9f3bb40571.png`,
  `${strapiAssetBaseUrl}/4_164d5dbc3e.png`
];

function ArticleContent() {
  return (
    <article className="flex flex-col gap-4">
      <h1>GRATEFUL GATHERINGS</h1>
      <p>
        Create memories worth celebrating with a Thanksgiving spread that
        harmoniously blends your favorite holiday flavors, incorporating the
        exceptional quality of Sub-Zero and Wolf bringing a feast in every bite.
      </p>

      <Carousel
        name={'Grateful Gatherings Photo Grid'}
        imageURLs={carouselImages}
      />

      <p>
        October 14, 2023 - Embracing the festive spirit of the upcoming holiday
        season, Sub-Zero and Wolf hosted a lavish Thanksgiving feast, delighting
        their guests with a celebration of delectable dishes. This cooking demo
        was conducted in the{' '}
        <Link href="https://my.matterport.com/show/?m=Boe2AFEuzJe">
          brands’ exclusive showroom at The Residences at Greenbelt
        </Link>{' '}
        in Makati City. Their corporate chefs excited guests by creating dishes
        inspired by well-loved Thanksgiving flavors, staging the ultimate feast.
      </p>

      <div>
        <div className="w-fit mx-auto md:float-left mb-6 md:mb-0 md:mr-6">
          <Image
            alt="Cranberry Brie Bites"
            className="object-contain object-center"
            height={180}
            src={`${strapiAssetBaseUrl}/5_46f38ce2ae.png`}
            width={270}
          />
        </div>
        <p className="text-justify">
          The celebration started with Cranberry Brie Bites, something familiar
          to the thanksgiving palate yet unique to the traditional menu. This
          combines the creamy goodness of brie cheese with the tart sweetness of
          cranberry sauce. Thanks to{' '}
          <Link href="https://subzero-wolf.com.ph/news-and-events/26659">
            Sub-Zero’s Dual Refrigeration System
          </Link>
          , the brie was kept in the perfect environment preventing it from
          drying out. The Wolf M Series Oven, using Convection Mode, baked this
          to perfection, achieving a warm, creamy interior and a golden brown
          exterior. Guests were impressed by the elegant simplicity of this
          dish, which they can easily recreate in their own homes.
        </p>
      </div>

      <Image
        alt="SZWF Image"
        className="object-contain object-center"
        height={573}
        src={`${strapiAssetBaseUrl}/6_a54c4bf494.png`}
        width={850}
      />

      <div className="flex flex-col-reverse md:flex-row gap-4 items-center max-w-2xl mx-auto my-8">
        <p className="text-justify">
          Next on the menu: Spiced Root Vegetable Soup, a comforting balance to
          Thanksgiving's rich offerings. This hearty soup brought earthy flavors
          from vegetables like lentils, sweet potatoes, onions, and carrots
          stored in the Sub-Zero refrigerator. Equipped with a{' '}
          <Link href="https://subzero-wolf.com.ph/news-and-events/26660">
            NASA-based Air Purification System
          </Link>
          , every Sub-Zero effectively removes molds, odors, and even ethylene
          gas, unlocking the maximum flavor and benefits of your fresh
          ingredients. To complete the preparation of this dish, the Wolf
          Induction Cooktop worked wonderfully with its{' '}
          <Link href="https://subzero-wolf.com.ph/news-and-events/26651">
            True Simmer function
          </Link>{' '}
          allowing for low but steady heat perfect for simmering soup.
        </p>
        <div className="w-[250px] md:w-[180px] shrink-0">
          <Image
            alt="Spiced Root Vegetable Soup"
            className="object-contain object-center"
            height={410}
            src={`${strapiAssetBaseUrl}/7_bd195e6b85.png`}
            width={250}
          />
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        <Image
          alt="SZWF Image"
          className="object-contain object-center"
          height={410}
          src={`${strapiAssetBaseUrl}/8_8705312525.png`}
          width={250}
        />
        <Image
          alt="SZWF Image"
          className="object-contain object-center"
          height={410}
          src={`${strapiAssetBaseUrl}/9_27b132b248.png`}
          width={250}
        />
        <Image
          alt="SZWF Image"
          className="object-contain object-center"
          height={410}
          src={`${strapiAssetBaseUrl}/10_4ca1eda015.png`}
          width={250}
        />
      </div>

      <p>
        Central to the culinary showcase, the chefs reimagined Thanksgiving
        turkey using premium ingredients—Roast Turkey with Port Wine Soaked Fig
        and Prosciutto Stuffing, served with Maple Apple Cognac Sauce. The Wolf
        Convection Steam Oven (CSO) astounded guests by significantly reducing
        cooking time without compromising quality. Traditionally, a 12-pound
        turkey takes 3 - 4 hours, involving constant basting to ensure the
        juices are fully incorporated. Transforming the game using the
        Convection Steam Mode, the whole turkey came out perfectly brown on the
        exterior while maintaining a juicy interior in just 1 hour and 5
        minutes. The convection heat allowed the turkey to achieve even browning
        while the steam effortlessly kept its meat moist and tender. The
        combination of the two cooking modes allowed for the cooking time to
        shorten highlighting the versatility of this{' '}
        <Link href="https://subzero-wolf.com.ph/news-and-events/24507">
          3-in-1 appliance.
        </Link>
      </p>

      <div className="flex flex-wrap justify-center gap-2">
        <Image
          alt="SZWF Image"
          className="object-contain object-center"
          height={410}
          src={`${strapiAssetBaseUrl}/11_412de73703.png`}
          width={250}
        />
        <Image
          alt="SZWF Image"
          className="object-contain object-center"
          height={410}
          src={`${strapiAssetBaseUrl}/12_1050f689ed.png`}
          width={250}
        />
        <Image
          alt="SZWF Image"
          className="object-contain object-center"
          height={410}
          src={`${strapiAssetBaseUrl}/13_0fb568e99d.png`}
          width={250}
        />
      </div>

      <p>
        Ending the celebration sweetly, the chefs showcased Pumpkin Crème Brûlée
        Cheesecake Bars, melding pumpkin pie, crème brûlée, and cheesecake
        flavors. Crafted in the Wolf M Series Oven’s{' '}
        <Link href="https://subzero-wolf.com.ph/news-and-events/26727">
          Dual Convection System
        </Link>
        , it ensured an evenly baked, buttery crust beneath a velvety pumpkin
        cheesecake layer. The delightful drink pairing, Iced Horchata and
        Cinnamon Cappuccino, blended the smoothness of horchata with the bold
        richness of classic cappuccino using the Wolf Coffee System.
      </p>

      <div className="mx-auto">
        <Image
          alt="SZWF Image"
          className="object-contain object-center"
          height={573}
          src={`${strapiAssetBaseUrl}/14_acd75d785e.png`}
          width={850}
        />
      </div>

      <p>
        Experience a celebration like no other and spend your holidays with
        Sub-Zero and Wolf by{' '}
        <Link href="https://calendly.com/fgi-kitchen-bookings?utm_source=Instagram&utm_medium=Bio_Link&utm_campaign=Calendly_Appointments&utm_content=SZWF">
          booking an appointment with us
        </Link>
        .
      </p>
    </article>
  );
}

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ArticleContent />
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps() {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = {
    attributes: {
      slug: '21524',
      news_title: 'Grateful Gatherings'
    }
  } as NewsAndEventsModel;

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export default Index;
